import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { RadioDot } from "@/components/RadioOption";
import useWizardState from "@/hooks/useWizardState";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import WizardStepIndicator from "@/pages/Messaging/Users/AddUserWizard/WizardStepIndicator";

interface TeamSetting {
  id: number;
  name: string;
  defaultVisitorLanguage: string | null;
}

interface WizardState {
  step: number;
  enableTranslation: boolean;
  strategy: "auto" | "team";
  teamSettings: TeamSetting[];
}

const INITIAL: WizardState = {
  step: 1,
  enableTranslation: false,
  strategy: "auto",
  teamSettings: []
};

const STEPS = ["Enable", "Strategy", "Teams", "Done"];

export default function LanguageSetupWizard() {
  const api = useApi();
  const navigate = useNavigate();
  const toastAlert = useToastAlert();

  const [languages, setLanguages] = useState<{ label: string; value: string }[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);

  const {
    wizardState,
    setWizardState,
    clearWizardState,
    isResuming,
    resumedFromLabel,
    resumeWizard,
    startFresh
  } = useWizardState<WizardState>("language-setup-wizard", INITIAL, 7);

  useEffect(() => {
    async function load() {
      try {
        const [settingsResp, teamsResp, langResp] = await Promise.all([
          api.messaging.get("SiteSettings"),
          api.messaging.get("Teams/List"),
          api.messaging.get("Language")
        ]);
        const [settings, teams, langs] = await Promise.all([
          settingsResp.json(),
          teamsResp.json(),
          langResp.json()
        ]);

        if (Array.isArray(langs)) setLanguages(langs);

        // Only seed state when not resuming a saved session
        if (!isResuming) {
          const teamSettings: TeamSetting[] = Array.isArray(teams)
            ? teams
                .filter((t: any) => !t.isDeleted)
                .map((t: any) => ({
                  id: t.id,
                  name: t.name,
                  defaultVisitorLanguage: t.defaultVisitorLanguage ?? null
                }))
            : [];

          const hasAnyTeamOverride = teamSettings.some(
            (t) => !!t.defaultVisitorLanguage
          );

          setWizardState((prev) => ({
            ...prev,
            enableTranslation: settings?.chatTranslationsEnabled ?? false,
            strategy: hasAnyTeamOverride ? "team" : "auto",
            teamSettings
          }));
        }
      } catch {
        // non-fatal — wizard still usable
      } finally {
        setLoadingData(false);
      }
    }
    load();
  }, [api.messaging]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveAll() {
    setSaving(true);
    try {
      // Patch site settings
      const settingsResp = await api.messaging.get("SiteSettings");
      const settings = await settingsResp.json();
      const siteOk = await api.messaging.post("SiteSettings", {
        ...settings,
        chatTranslationsEnabled: wizardState.enableTranslation
      });
      if (!siteOk.ok) throw new Error("site settings");

      // Save team overrides
      for (const team of wizardState.teamSettings) {
        const targetLang =
          wizardState.strategy === "auto" ? null : team.defaultVisitorLanguage ?? null;
        const teamResp = await api.messaging.get(`Teams/${team.id}`);
        const teamData = await teamResp.json();
        await api.messaging.put(`Teams/${team.id}`, {
          ...teamData,
          defaultVisitorLanguage: targetLang
        });
      }

      clearWizardState();
      toastAlert.displayToast(Severity.Success, "Translation settings saved.");
      setWizardState((prev) => ({ ...prev, step: 4 }));
    } catch {
      toastAlert.displayToast(
        Severity.Error,
        "Failed to save settings. Please try again."
      );
    } finally {
      setSaving(false);
    }
  }

  const { step } = wizardState;

  return (
    <div className="max-w-3xl mx-auto p-8">
      {/* Resume banner */}
      {isResuming && (
        <div className="mb-6 p-4 bg-primary-bg border border-cornflower-blue-200 rounded-lg flex items-center justify-between gap-4">
          <p className="text-sm text-primary-active">
            You have an unfinished setup from {resumedFromLabel}. Resume where you left off?
          </p>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={resumeWizard}
              className="text-sm px-3 py-1 bg-primary text-white rounded hover:bg-primary"
            >
              Resume
            </button>
            <button
              onClick={startFresh}
              className="text-sm px-3 py-1 border border-slate-300 rounded hover:bg-slate-50"
            >
              Start fresh
            </button>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-800 mb-1">
          Language &amp; Translation Setup
        </h1>
        <p className="text-slate-500 text-sm">
          Configure how Velaro translates messages between your agents and visitors. This
          wizard is additive — it won&apos;t clear any existing configurations.
        </p>
      </div>

      <WizardStepIndicator stepLabels={STEPS} activeStep={step} />

      <div className="mt-10">
        {step === 1 && (
          <Step1Enable
            enabled={wizardState.enableTranslation}
            onChange={(v) =>
              setWizardState((prev) => ({ ...prev, enableTranslation: v }))
            }
            onNext={() => setWizardState((prev) => ({ ...prev, step: 2 }))}
            onCancel={() => navigate("/site_settings")}
          />
        )}

        {step === 2 && (
          <Step2Strategy
            strategy={wizardState.strategy}
            enableTranslation={wizardState.enableTranslation}
            onChange={(v) =>
              setWizardState((prev) => ({ ...prev, strategy: v }))
            }
            onBack={() => setWizardState((prev) => ({ ...prev, step: 1 }))}
            onNext={() => {
              if (wizardState.strategy === "team") {
                setWizardState((prev) => ({ ...prev, step: 3 }));
              } else {
                saveAll();
              }
            }}
            saving={saving}
          />
        )}

        {step === 3 && (
          <Step3Teams
            teams={wizardState.teamSettings}
            languages={languages}
            loading={loadingData}
            onChange={(teams) =>
              setWizardState((prev) => ({ ...prev, teamSettings: teams }))
            }
            onBack={() => setWizardState((prev) => ({ ...prev, step: 2 }))}
            onSave={saveAll}
            saving={saving}
          />
        )}

        {step === 4 && (
          <StepDone
            enableTranslation={wizardState.enableTranslation}
            strategy={wizardState.strategy}
            teamSettings={wizardState.teamSettings}
            languages={languages}
            onGoToSettings={() => navigate("/site_settings")}
            onGoToTeams={() => navigate("/messaging/teams")}
          />
        )}
      </div>
    </div>
  );
}

// ── Step 1 ──────────────────────────────────────────────────────────────────

function Step1Enable({
  enabled,
  onChange,
  onNext,
  onCancel
}: {
  enabled: boolean;
  onChange(v: boolean): void;
  onNext(): void;
  onCancel(): void;
}) {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-800">
          Enable automatic translation
        </h2>
        <p className="text-sm text-slate-600">
          When enabled, Velaro automatically translates messages in both directions —
          visitor messages appear in each agent&apos;s preferred language, and agent
          replies are translated back into the visitor&apos;s language. Works across
          Web, WhatsApp, SMS, Facebook, Instagram, TikTok, RCS, and Email.
        </p>

        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => onChange(!enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              enabled ? "bg-primary" : "bg-slate-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
          <span className="text-sm font-medium text-slate-700">
            {enabled ? "Translation is ON" : "Translation is OFF"}
          </span>
        </div>

        {/* Scenario cards */}
        <div className="grid grid-cols-1 gap-3 pt-2">
          {[
            {
              icon: "🌍",
              title: "Auto-bidirectional (most common)",
              desc: "Visitor writes Spanish, agent sees English. Agent writes English, visitor sees Spanish. Language is detected automatically."
            },
            {
              icon: "🏢",
              title: "Dedicated language teams",
              desc: "You have a French team and a Spanish team. Lock each team to its language so translation is consistent regardless of browser locale — works alongside auto-detection."
            },
            {
              icon: "👥",
              title: "Multilingual agents",
              desc: "Each agent sets their own language in their profile. A Spanish-speaking agent sees Spanish; an English-speaking agent sees English — same visitor."
            }
          ].map((s) => (
            <div
              key={s.title}
              className="flex gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
            >
              <span className="text-2xl">{s.icon}</span>
              <div>
                <p className="text-sm font-medium text-slate-800">{s.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50"
        >
          ← Back to Settings
        </button>
        <button
          type="button"
          onClick={onNext}
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

// ── Step 2 ──────────────────────────────────────────────────────────────────

function Step2Strategy({
  strategy,
  enableTranslation,
  onChange,
  onBack,
  onNext,
  saving
}: {
  strategy: "auto" | "team";
  enableTranslation: boolean;
  onChange(v: "auto" | "team"): void;
  onBack(): void;
  onNext(): void;
  saving: boolean;
}) {
  const options: { value: "auto" | "team"; title: string; desc: string; example: string }[] = [
    {
      value: "auto",
      title: "Auto-detect visitor language",
      desc: "Velaro reads the visitor's browser language and incoming messages to detect their language. Works for most sites.",
      example: "A visitor from Mexico writes in Spanish → auto-detected, agent sees English."
    },
    {
      value: "team",
      title: "Set a fixed language per team",
      desc: "You have teams organized by language (Spanish team, French team, etc.). Set the target language per team so translation is reliable regardless of browser detection.",
      example: "Your French team always translates to fr-CA — even if the visitor's browser reports 'en'."
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 min-h-[320px]">
        <h2 className="text-lg font-semibold text-slate-800">
          How should visitor language be determined?
        </h2>

        {!enableTranslation && (
          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Translation is disabled — this strategy will take effect when you enable it.
          </div>
        )}

        <div className="space-y-3">
          {options.map((opt) => (
            <label
              key={opt.value}
              className={`flex gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                strategy === opt.value
                  ? "border-primary bg-primary-bg"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <input type="radio" name="strategy" value={opt.value} checked={strategy === opt.value} onChange={() => onChange(opt.value)} className="sr-only" />
              <RadioDot checked={strategy === opt.value} className="mt-1" />
              <div>
                <p className="text-sm font-semibold text-slate-800">{opt.title}</p>
                <p className="text-xs text-slate-500 mt-1">{opt.desc}</p>
                <p className="text-xs text-primary mt-1 italic">
                  e.g. {opt.example}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={saving}
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary disabled:opacity-50"
        >
          {saving
            ? "Saving…"
            : strategy === "team"
            ? "Next → Configure Teams"
            : "Save & Finish"}
        </button>
      </div>
    </div>
  );
}

// ── Step 3 ──────────────────────────────────────────────────────────────────

function Step3Teams({
  teams,
  languages,
  loading,
  onChange,
  onBack,
  onSave,
  saving
}: {
  teams: TeamSetting[];
  languages: { label: string; value: string }[];
  loading: boolean;
  onChange(teams: TeamSetting[]): void;
  onBack(): void;
  onSave(): void;
  saving: boolean;
}) {
  function setTeamLang(teamId: number, lang: string | null) {
    onChange(
      teams.map((t) =>
        t.id === teamId ? { ...t, defaultVisitorLanguage: lang } : t
      )
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 min-h-[320px]">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">
            Set visitor language per team
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Set the visitor language for each team that has a known language. Leave
            blank to use auto-detection for that team.
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400">Loading teams…</p>
        ) : teams.length === 0 ? (
          <p className="text-sm text-slate-400">No teams found.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {teams.map((team) => (
              <div
                key={team.id}
                className="flex items-center justify-between py-3 gap-4"
              >
                <span className="text-sm font-medium text-slate-700 min-w-0 truncate">
                  {team.name}
                </span>
                <select
                  aria-label={`Visitor language for ${team.name}`}
                  value={team.defaultVisitorLanguage ?? ""}
                  onChange={(e) =>
                    setTeamLang(team.id, e.target.value || null)
                  }
                  className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 w-48 shrink-0"
                >
                  <option value="">— auto-detect —</option>
                  {languages.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save & Finish"}
        </button>
      </div>
    </div>
  );
}

// ── Done ────────────────────────────────────────────────────────────────────

function StepDone({
  enableTranslation,
  strategy,
  teamSettings,
  languages,
  onGoToSettings,
  onGoToTeams
}: {
  enableTranslation: boolean;
  strategy: "auto" | "team";
  teamSettings: TeamSetting[];
  languages: { label: string; value: string }[];
  onGoToSettings(): void;
  onGoToTeams(): void;
}) {
  const overrides = teamSettings.filter((t) => t.defaultVisitorLanguage);

  function langLabel(code: string) {
    return languages.find((l) => l.value === code)?.label ?? code;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <div className="flex items-center gap-3">
          <span className="text-3xl">✅</span>
          <h2 className="text-xl font-semibold text-slate-800">
            Translation configured
          </h2>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                enableTranslation
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {enableTranslation ? "ON" : "OFF"}
            </span>
            <span className="text-slate-700">Account-level translation</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-bg text-primary-active">
              {strategy === "auto" ? "Auto-detect" : "Team overrides"}
            </span>
            <span className="text-slate-700">Visitor language strategy</span>
          </div>

          {strategy === "team" && overrides.length > 0 && (
            <div className="mt-2 pl-4 border-l-2 border-cornflower-blue-200 space-y-1">
              {overrides.map((t) => (
                <p key={t.id} className="text-xs text-slate-600">
                  <span className="font-medium">{t.name}</span>
                  {" → "}
                  {langLabel(t.defaultVisitorLanguage!)}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-3">
            You can update these settings at any time in Site Settings or the Teams page.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onGoToSettings}
              className="text-sm px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Go to Site Settings
            </button>
            <button
              type="button"
              onClick={onGoToTeams}
              className="text-sm px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Go to Teams
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
